// useContactOptions.ts
import { useState, useEffect, useCallback } from 'react';
import { useWorkspaces } from './useWorkspaces';
import { useToast } from './use-toast';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import type { ContactOptionCategory } from '@/types/contact';

const DEFAULT_OPTIONS = {
  relationship: ['Friends & Family', 'Acquaintance', 'Business', 'Referral', 'Cold'],
  type: ['Prospect', 'Referrer', 'Client', 'Team', 'Competition', 'Vendor'],
  rank: ['A', 'B', 'C'],
  status: ['Not Yet Contacted', 'In Process', 'Active', 'Former', 'Not Interested Now', 'Do Not Contact'],
  call_outcome: ['Left Message', 'Meaningful Conversation', 'New Recommender', 'New Referral', 'Sale', 'New Prospect', 'Not Interested Now', 'No Answer', 'Wrong Number'],
};

export function useContactOptions() {
  const [options, setOptions] = useState<Record<ContactOptionCategory, string[]>>(DEFAULT_OPTIONS);
  const [loading, setLoading] = useState(false);
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();

  const fetchOptions = useCallback(async () => {
    if (!currentWorkspace?.id) return;
    
    setLoading(true);
    
    try {
      console.log('[useContactOptions] Fetching contact options from server');
      
      const { data, error } = await supabase
        .from('contact_options')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .order('value');

      if (error) throw error;
      
      const groupedOptions = data.reduce((acc, option) => {
        const category = option.category as ContactOptionCategory;
        if (!acc[category]) acc[category] = [];
        acc[category].push(option.value);
        return acc;
      }, {} as Record<ContactOptionCategory, string[]>);

      const mergedOptions = {
        relationship: [...new Set([...DEFAULT_OPTIONS.relationship, ...(groupedOptions.relationship || [])])],
        type: [...new Set([...DEFAULT_OPTIONS.type, ...(groupedOptions.type || [])])],
        rank: [...new Set([...DEFAULT_OPTIONS.rank, ...(groupedOptions.rank || [])])],
        status: [...new Set([...DEFAULT_OPTIONS.status, ...(groupedOptions.status || [])])],
        call_outcome: [...new Set([...DEFAULT_OPTIONS.call_outcome, ...(groupedOptions.call_outcome || [])])],
      };
      
      console.log('[useContactOptions] Options fetched successfully');
      setOptions(mergedOptions);
      
    } catch (error) {
      console.error('Error fetching contact options:', error);
      captureError(error as Error, { feature: 'contact-options-fetch' });
      toast({
        title: "Error",
        description: "Failed to load contact options. Using defaults.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id, toast]);

  useEffect(() => {
    if (currentWorkspace?.id) {
      fetchOptions();
    }
  }, [currentWorkspace?.id, fetchOptions]);

  return { 
    options, 
    loading,
    refreshOptions: () => fetchOptions()
  };
}